/**
 * Session 60.17 · HoursSharePrompt
 *
 * The post-redemption share modal. Renders only when `scan` is set. Copy
 * is verbatim from Cam's spec:
 *
 *   "Perk unlocked 🎉
 *    You used your Métier member perk at [Partner Name].
 *
 *    Share the love
 *    Support [Partner Name] and show your network where you're working today.
 *
 *    [ Share to Socials ] [ Not now ]"
 *
 * Sharing is always optional. Never blocks redemption. Never claims a
 * post was published — the native share sheet opening records "started".
 */
import React from "react";

export default function HoursSharePrompt({ scan, onShare, onDismiss }) {
  if (!scan) return null;
  const partnerName = scan.partner_name || "a Métier Hours partner";
  const perkLabel = (scan.perk_label || "").trim();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="hours-share-title"
      data-testid="hours-share-prompt"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(9,14,32,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
        backdropFilter: "blur(4px)",
      }}
      onClick={(e) => {
        // click on backdrop = dismiss (soft, non-suppressing? we route
        // through dismiss so it stays consistent with the button).
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div
        style={{
          background: "#FAF8F3",
          color: "#1B2340",
          maxWidth: 460,
          width: "100%",
          borderRadius: 18,
          padding: 28,
          boxShadow: "0 30px 80px rgba(9,14,32,0.4)",
          border: "1px solid rgba(184,145,58,0.25)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#B8913A",
            fontWeight: 600,
          }}
        >
          Métier Hours
        </div>
        <h2
          id="hours-share-title"
          data-testid="hours-share-title"
          style={{
            fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
            fontWeight: 500,
            fontSize: 30,
            lineHeight: 1.15,
            margin: "6px 0 12px",
          }}
        >
          Perk unlocked <span aria-hidden="true">&#x1F389;</span>
        </h2>
        <p
          data-testid="hours-share-body"
          style={{ fontSize: 15, lineHeight: 1.6, margin: 0, color: "rgba(27,35,64,0.8)" }}
        >
          You used your Métier member perk at <strong>{partnerName}</strong>
          {perkLabel ? <> — <span style={{ color: "#B8913A", fontWeight: 600 }}>{perkLabel}</span></> : null}.
        </p>

        <div
          style={{
            marginTop: 20,
            padding: "14px 16px",
            borderRadius: 12,
            background: "rgba(184,145,58,0.10)",
            border: "1px solid rgba(184,145,58,0.28)",
          }}
        >
          <div
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 20,
              fontWeight: 500,
              marginBottom: 4,
            }}
          >
            Share the love.
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "rgba(27,35,64,0.75)" }}>
            Support <strong>{partnerName}</strong> and show your network where you&#39;re working today.
          </div>
        </div>

        <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={onShare}
            data-testid="hours-share-cta"
            style={{
              padding: "13px 18px",
              borderRadius: 999,
              border: "1px solid rgba(139,106,34,0.55)",
              background: "linear-gradient(135deg,#D6B559 0%,#B8913A 55%,#8B6A22 100%)",
              color: "#FFF8E4",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: "pointer",
              width: "100%",
            }}
          >
            Share to Socials
          </button>
          <button
            type="button"
            onClick={onDismiss}
            data-testid="hours-share-dismiss"
            style={{
              padding: "11px 18px",
              borderRadius: 999,
              border: "1px solid rgba(27,35,64,0.15)",
              background: "transparent",
              color: "rgba(27,35,64,0.75)",
              fontSize: 12.5,
              fontWeight: 600,
              letterSpacing: "0.04em",
              cursor: "pointer",
              width: "100%",
            }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
